import { jwtDecode } from 'jwt-decode';
import { biometricService } from './biometricService';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const authService = {
    setTokens(accessToken, refreshToken) {
        localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        
        // Also decode the new access token and update session storage
        try {
            const decoded = this.decodeToken(accessToken);
            console.log('setTokens() - Decoded new token:', decoded);
            
            if (decoded) {
                // Update other relevant fields
                if (decoded.user_type) {
                    sessionStorage.setItem('user_type', decoded.user_type);
                }
                if (decoded.is_admin !== undefined) {
                    sessionStorage.setItem('is_admin', decoded.is_admin ? 'true' : 'false');
                }
                if (decoded.is_manager !== undefined) {
                    sessionStorage.setItem('is_manager', decoded.is_manager ? 'true' : 'false');
                }
                if (decoded.is_child !== undefined) {
                    sessionStorage.setItem('is_child', decoded.is_child ? 'true' : 'false');
                }
            }
        } catch (error) {
            console.error('Error updating session storage from new token:', error);
        }
    },

    clearTokens() {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        // Clear all session storage
        sessionStorage.removeItem('user_id');
        sessionStorage.removeItem('player_id');
        sessionStorage.removeItem('first_name');
        sessionStorage.removeItem('last_name');
        sessionStorage.removeItem('user_type');
        sessionStorage.removeItem('is_parent');
        sessionStorage.removeItem('has_children');
        sessionStorage.removeItem('selected_team_id');
        sessionStorage.removeItem('selected_team_name');
        sessionStorage.removeItem('selected_team_type');
        sessionStorage.removeItem('selected_team_age_group');
    sessionStorage.removeItem('selected_team_league');
        sessionStorage.removeItem('shirt_number');
        sessionStorage.removeItem('is_first_login');
        sessionStorage.removeItem('is_manager');
        sessionStorage.removeItem('is_admin');
        sessionStorage.removeItem('permanent_keeper');
        sessionStorage.removeItem('is_player_manager');
        sessionStorage.removeItem('is_child');
    },

    getAccessToken() {
        return localStorage.getItem(AUTH_TOKEN_KEY);
    },

    getRefreshToken() {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    },

    isAuthenticated() {
        const token = this.getAccessToken();
    
        
        if (!token) return false;
        
        try {
            const decoded = jwtDecode(token);
            const isValid = decoded.exp > Date.now() / 1000;
            return isValid;
        } catch (error) {
            console.error('Token validation error:', error);
            return false;
        }
    },

    logout() {
        this.clearTokens();
        // Optionally clear biometric credentials on logout (uncomment if desired)
        // this.disableBiometricLogin();
        window.location.href = '/';
    },

    isAdmin() {
        const token = this.getAccessToken();
        
        if (!token) return false;
        
        try {
            const decoded = this.decodeToken(token);
            
            // First check JWT token for is_admin flag
            const jwtAdmin = decoded?.is_admin === true;
            
            // Then check if user_type is manager or admin in session storage
            const userType = sessionStorage.getItem('user_type');
            const sessionAdmin = sessionStorage.getItem('is_admin') === 'true';
            const isAdminUserType = userType === 'admin' || userType === 'superadmin';
            
            // Return true if any of the admin checks pass
            return jwtAdmin || sessionAdmin || isAdminUserType;
        } catch (error) {
            console.error('Error checking admin status:', error);
            return false;
        }
    },

    isManager() {
        // First check session storage for is_manager flag
        const sessionManager = sessionStorage.getItem('is_manager') === 'true';
        if (sessionManager) return true;
        
        // Check if user_type is manager or coach
        const userType = sessionStorage.getItem('user_type');
        if (userType === 'manager' || userType === 'coach') return true;

        // Then check JWT token
        const token = this.getAccessToken();
        if (!token) return false;
        
        try {
            const decoded = this.decodeToken(token);
            return decoded?.is_manager === true;
        } catch (error) {
            console.error('Error checking manager status:', error);
            return false;
        }
    },

    isPro() {
        const token = this.getAccessToken();
        if (!token) return false;
        
        try {
            const decoded = this.decodeToken(token);
            return decoded?.is_pro === true;
        } catch (error) {
            console.error('Error checking pro status:', error);
            return false;
        }
    },
    


    isParent() {
        // First check session storage for user_type
        const userType = sessionStorage.getItem('user_type');
        if (userType === 'parent') return true;
        
        // Then check if is_parent flag is set in session storage
        const sessionParent = sessionStorage.getItem('is_parent') === 'true';
        if (sessionParent) return true;
        
        // Finally check JWT token
        const token = this.getAccessToken();
        if (!token) return false;
        
        try {
            const decoded = this.decodeToken(token);
            return decoded?.user_type === 'parent';
        } catch (error) {
            console.error('Error checking parent status:', error);
            return false;
        }
    },

    isChild() {
        // Check session storage first
        const sessionChild = sessionStorage.getItem('is_child') === 'true';
        if (sessionChild) return true;
        
        // Check JWT token
        const token = this.getAccessToken();
        if (!token) return false;
        
        try {
            const decoded = this.decodeToken(token);
            return decoded?.is_child === true;
        } catch (error) {
            console.error('Error checking child status:', error);
            return false;
        }
    },

    canRespondToMatches(childId) {
        // Only check for parent users
        if (!this.isParent()) {
            console.log('canRespondToMatches: Not a parent user');
            return false;
        }
        
        const token = this.getAccessToken();
        
        if (!token) {
            console.log('canRespondToMatches: No access token');
            return false;
        }
        
        try {
            const decoded = this.decodeToken(token);
            console.log('canRespondToMatches: Decoded token:', {
                childId: childId,
                child_permissions: decoded?.child_permissions,
                user_id: decoded?.user_id,
                user_type: decoded?.user_type
            });
            
            // Check if child_permissions exists and contains the child ID
            if (decoded?.child_permissions && typeof decoded.child_permissions === 'object') {
                const result = decoded.child_permissions[childId] === true;
                console.log('canRespondToMatches: Result:', result);
                return result;
            }
            
            console.log('canRespondToMatches: No child_permissions found');
            return false;
        } catch (error) {
            console.error('Error checking match response permissions:', error);
            return false;
        }
    },
    
    getApprovalStatus(childId) {
        // Only check for parent users
        if (!this.isParent()) return null;
        
        const token = this.getAccessToken();
        
        if (!token) return null;
        
        try {
            const decoded = this.decodeToken(token);
            
            // Check if approval_statuses exists and contains the child ID
            if (decoded?.approval_statuses && typeof decoded.approval_statuses === 'object') {
                return decoded.approval_statuses[childId];
            }
            
            return null;
        } catch (error) {
            console.error('Error checking approval status:', error);
            return null;
        }
    },

    // Refresh JWT token to get updated Pro status after payment
    async refreshTokenAfterPayment() {
        try {
            const refreshToken = this.getRefreshToken();
            if (!refreshToken) {
                console.error('No refresh token available');
                return false;
            }

            const response = await fetch('/api/refresh_token.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    refresh_token: refreshToken
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.access_token) {
                    // Update the access token
                    localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
                    

                    
                    console.log('Token refreshed successfully after payment');
                    
                    // Dispatch event to notify components of Pro status change
                    window.dispatchEvent(new CustomEvent('token-refreshed'));
                    
                    return true;
                }
            }
            
            console.error('Failed to refresh token after payment');
            return false;
        } catch (error) {
            console.error('Error refreshing token after payment:', error);
            return false;
        }
    },

    // Removed updateProStatus method for security - Pro status should only come from JWT token

    isSuperAdmin() {
        const token = this.getAccessToken();
        if (!token) return false;
        
        try {
            const decoded = this.decodeToken(token);
            return parseInt(decoded?.user_id) === 1;
        } catch (error) {
            console.error('Error checking superadmin status:', error);
            return false;
        }
    },

    decodeToken(token) {
        try {
            // Split the token and get the payload part
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            const decoded = JSON.parse(jsonPayload);
            return decoded;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    },

    setAdminStatus(isAdmin) {
        const token = this.getAccessToken();
        if (!token) return;
        
        try {
            // Decode current token
            const decoded = this.decodeToken(token);
            if (!decoded || !decoded.user_id) {
                console.error('Invalid token structure');
                return;
            }
            
            // Create new token preserving user_id and other fields
            const newToken = this.createDevToken({
                ...decoded,
                is_admin: isAdmin
            });
            
            // Update tokens
            localStorage.setItem(AUTH_TOKEN_KEY, newToken);
            
            // Force refresh app state
            window.dispatchEvent(new Event('admin-status-changed'));
            
        } catch (error) {
            console.error('Error updating admin status:', error);
        }
    },

    // Development helper to create test tokens
    createDevToken(payload) {
        // Ensure all required fields are present
        const newPayload = {
            iss: 'football-app',  // Match the issuer from jwt_handler.php
            iat: Math.floor(Date.now() / 1000),
            exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour expiry
            user_id: payload.user_id || payload.player_id, // Try both user_id and player_id
            is_admin: payload.is_admin
        };

        console.log('createDevToken: Creating token with payload:', newPayload);

        // Create proper JWT structure
        const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
        const content = btoa(JSON.stringify(newPayload));
        // Use a consistent dev signature
        const signature = btoa('dev-signature-key-' + content);
        
        return `${header}.${content}.${signature}`;
    },

    /**
     * Check if biometric login is available and credentials are stored
     */
    async canUseBiometricLogin() {
        try {
            const isAvailable = await biometricService.isAvailable();
            if (!isAvailable) return false;
            
            // Check if credentials exist - this may throw KeychainError if not found, which is normal
            const hasCredentials = await biometricService.hasStoredCredentials();
            return hasCredentials;
        } catch (error) {
            // KeychainError when credentials don't exist is expected, not an actual error
            if (error.message?.includes('KeychainError') || error.errorMessage?.includes('KeychainError')) {
                return false; // No credentials stored yet
            }
            console.error('Error checking biometric login availability:', error);
            return false;
        }
    },

    /**
     * Store credentials for biometric login
     */
    async enableBiometricLogin(phone, password) {
        try {
            await biometricService.storeCredentials(phone, password);
            // Store preference that user has enabled biometric login
            localStorage.setItem('biometric_enabled', 'true');
            return true;
        } catch (error) {
            console.error('Error enabling biometric login:', error);
            throw error;
        }
    },

    /**
     * Disable biometric login and remove stored credentials
     */
    async disableBiometricLogin() {
        try {
            await biometricService.deleteCredentials();
            localStorage.removeItem('biometric_enabled');
            return true;
        } catch (error) {
            console.error('Error disabling biometric login:', error);
            return false;
        }
    },

    /**
     * Check if biometric login is enabled by user preference
     */
    isBiometricEnabled() {
        return localStorage.getItem('biometric_enabled') === 'true';
    }
}; 